(function () {
  if (customElements.get('cart-discount')) {
    return;
  }

  const SELECTORS = {
    input: '[data-cart-discount-input]',
    applyBtn: '[data-cart-discount-apply]',
    removeBtn: '[data-cart-discount-remove]',
    form: '[data-cart-discount-form]',
    appliedList: '[data-cart-discount-applied-list]',
    status: '[data-cart-discount-status]',
  };

  class CartDiscount extends HTMLElement {
    connectedCallback() {
      this.cartDrawer = this.closest('cart-drawer') || document.getElementById('CartDrawer');
      this.form = this.querySelector(SELECTORS.form);
      this.input = this.querySelector(SELECTORS.input);
      this.applyBtn = this.querySelector(SELECTORS.applyBtn);
      this.statusEl = this.querySelector(SELECTORS.status);

      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleApply = this.handleApply.bind(this);
      this.handleClick = this.handleClick.bind(this);

      this.form?.addEventListener('submit', this.handleSubmit);
      this.applyBtn?.addEventListener('click', this.handleApply);
      this.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.form?.removeEventListener('submit', this.handleSubmit);
      this.applyBtn?.removeEventListener('click', this.handleApply);
      this.removeEventListener('click', this.handleClick);
    }

    get strings() {
      return window.auroraThemeLocales?.cartStrings || {};
    }

    handleSubmit(event) {
      event.preventDefault();
      this.handleApply();
    }

    handleClick(event) {
      const removeBtn = event.target.closest(SELECTORS.removeBtn);
      if (!removeBtn || removeBtn.disabled) {
        return;
      }
      event.preventDefault();
      this.handleRemove(removeBtn);
    }

    getActiveDiscountCodes() {
      const codes = new Set();
      this.querySelectorAll('[data-cart-discount-applied][data-discount-code]').forEach((card) => {
        const code = card.dataset.discountCode;
        if (code) {
          codes.add(code);
        }
      });
      return [...codes];
    }

    async handleApply() {
      const code = this.input?.value?.trim();
      if (!code) {
        this.showFeedback('error', this.strings.discountEmpty || 'Enter a discount code');
        return;
      }

      const existing = this.getActiveDiscountCodes();
      if (existing.some((c) => c.toLowerCase() === code.toLowerCase())) {
        this.showFeedback('error', this.strings.discountAlreadyApplied || 'This discount is already applied');
        return;
      }

      const discount = [...existing, code].join(',');
      await this.updateCartDiscount(discount, { appliedCode: code });
    }

    async handleRemove(removeBtn) {
      const codeToRemove = removeBtn.dataset.discountCode;
      if (!codeToRemove) {
        return;
      }

      const codes = this.getActiveDiscountCodes().filter((code) => code !== codeToRemove);
      await this.updateCartDiscount(codes.join(','), {
        removeBtn,
        removedCode: codeToRemove,
      });
    }

    async updateCartDiscount(discount, options = {}) {
      const cartDrawer = this.cartDrawer;
      if (!cartDrawer || !window.routes?.cart_update_url) {
        return;
      }

      const sectionId = cartDrawer.dataset.sectionId;
      const isRemoving = Boolean(options.removeBtn);

      this.setBusy(true, options.removeBtn);
      cartDrawer.classList.add('is-cart-loading');

      try {
        const response = await fetch(window.routes.cart_update_url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            discount,
            sections: sectionId,
            sections_url: window.location.pathname,
          }),
        });

        const data = await response.json();

        if (!response.ok || data.errors) {
          const message =
            data.description ||
            data.message ||
            (typeof data.errors === 'string' ? data.errors : this.strings.discountError);
          this.showFeedback('error', message);
          return;
        }

        const cart = await this.fetchCart();

        if (!cart) {
          this.showFeedback('error', this.strings.error);
          return;
        }

        if (isRemoving) {
          const successMessage = this.formatMessage(this.strings.discountRemoved, {
            code: options.removedCode,
          });
          this.showNotification('success', successMessage);
          await this.refreshCartDrawer(cartDrawer, data, sectionId);
          this.getDiscountElement(cartDrawer)?.setInlineStatus('success', successMessage);
          return;
        }

        const verification = this.verifyDiscount(cart, options.appliedCode);

        if (!verification.valid) {
          const message =
            verification.reason === 'not_applicable'
              ? this.formatMessage(this.strings.discountNotApplicable, { code: options.appliedCode })
              : this.strings.discountInvalid || 'Enter a valid discount code';

          const codesWithoutFailed = this.getActiveDiscountCodes().filter(
            (c) => c.toLowerCase() !== options.appliedCode?.toLowerCase()
          );
          const rollbackResponse = await fetch(window.routes.cart_update_url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              discount: codesWithoutFailed.join(','),
              sections: sectionId,
              sections_url: window.location.pathname,
            }),
          });
          const rollbackData = await rollbackResponse.json();
          await this.refreshCartDrawer(cartDrawer, rollbackData, sectionId);
          this.showFeedback('error', message);
          return;
        }

        this.showNotification('success', verification.message);
        await this.refreshCartDrawer(cartDrawer, data, sectionId);
        this.getDiscountElement(cartDrawer)?.setInlineStatus('success', verification.message);
      } catch (error) {
        this.showFeedback('error', this.strings.error || 'There was an error while updating your cart. Please try again.');
      } finally {
        this.setBusy(false, options.removeBtn);
        cartDrawer.classList.remove('is-cart-loading');
      }
    }

    async fetchCart() {
      const response = await fetch(`${window.routes.cart_url}.js`);
      if (!response.ok) {
        return null;
      }
      return response.json();
    }

    verifyDiscount(cart, code) {
      if (!code) {
        return { valid: true, message: this.strings.discountSuccessOrder || 'Discount applied' };
      }

      const normalizedCode = code.toLowerCase();
      const discountCodes = cart.discount_codes || [];
      let codeOnCart = false;
      let applicable = true;

      discountCodes.forEach((entry) => {
        const entryCode = (entry.code || entry || '').toLowerCase();
        if (entryCode === normalizedCode) {
          codeOnCart = true;
          if (entry.applicable === false) {
            applicable = false;
          }
        }
      });

      const details = this.getDiscountDetails(cart, code);
      const hasEffect = details.products.length > 0 || details.isOrderWide;

      if (!codeOnCart) {
        const hasApplication =
          (cart.cart_level_discount_applications || []).some(
            (app) => (app.title || '').toLowerCase() === normalizedCode
          ) || details.products.length > 0;

        if (!hasApplication) {
          return { valid: false, reason: 'invalid' };
        }
      }

      if (!applicable || !hasEffect) {
        return { valid: false, reason: 'not_applicable' };
      }

      return {
        valid: true,
        message: this.buildSuccessMessage(code, details),
      };
    }

    getDiscountDetails(cart, code) {
      const normalizedCode = code.toLowerCase();
      const products = [];

      (cart.items || []).forEach((item) => {
        (item.discounts || []).forEach((discount) => {
          if ((discount.title || '').toLowerCase() === normalizedCode) {
            products.push(item.product_title || item.title);
          }
        });
      });

      const isOrderWide = (cart.cart_level_discount_applications || []).some(
        (app) => (app.title || '').toLowerCase() === normalizedCode
      );

      return {
        products: [...new Set(products)],
        isOrderWide,
      };
    }

    buildSuccessMessage(code, details) {
      const products = details.products.join(', ');

      if (details.products.length > 0 && details.isOrderWide) {
        return this.formatMessage(this.strings.discountSuccessProductsAndOrder, {
          code,
          products,
        });
      }

      if (details.products.length > 0) {
        return this.formatMessage(this.strings.discountSuccessProducts, { code, products });
      }

      return this.formatMessage(this.strings.discountSuccessOrder, { code });
    }

    formatMessage(template, vars) {
      if (!template) {
        return '';
      }
      return Object.entries(vars).reduce(
        (message, [key, value]) => message.replaceAll(`{{ ${key} }}`, value),
        template
      );
    }

    parseDrawerNode(sectionHtml) {
      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const template = doc.querySelector('[data-sidebar-template="CartDrawer"]');
      if (!template) {
        return null;
      }
      return template.content?.firstElementChild?.cloneNode(true) || null;
    }

    async refreshCartDrawer(cartDrawer, updateData, sectionId) {
      const sectionHtml = updateData?.sections?.[sectionId];

      if (sectionHtml && typeof cartDrawer.mount === 'function') {
        const node = this.parseDrawerNode(sectionHtml);
        if (node) {
          return new Promise((resolve) => {
            const timeout = window.setTimeout(resolve, 5000);
            const handleUpdate = () => {
              window.clearTimeout(timeout);
              cartDrawer.off('update-nodes', handleUpdate);
              resolve();
            };

            cartDrawer.on('update-nodes', handleUpdate);
            cartDrawer.lastDrawerBody = node;
            cartDrawer.element = node;
            cartDrawer.mount();
            cartDrawer.emit('update-nodes', { node });
            cartDrawer.updateEmptyStatus?.();
          });
        }
      }

      return new Promise((resolve) => {
        const timeout = window.setTimeout(resolve, 5000);
        const handleUpdate = () => {
          window.clearTimeout(timeout);
          cartDrawer.off('update-nodes', handleUpdate);
          resolve();
        };

        cartDrawer.on('update-nodes', handleUpdate);
        cartDrawer.update();
      });
    }

    setBusy(isBusy, removeBtn) {
      if (removeBtn) {
        removeBtn.classList.toggle('is-loading', isBusy);
        removeBtn.disabled = isBusy;
        this.querySelectorAll(SELECTORS.removeBtn).forEach((btn) => {
          if (btn !== removeBtn) {
            btn.disabled = isBusy;
          }
        });
      } else {
        this.classList.toggle('is-loading', isBusy);
        this.applyBtn?.classList.toggle('loading', isBusy);
        if (this.applyBtn) {
          this.applyBtn.disabled = isBusy;
        }
      }

      if (this.input) {
        this.input.disabled = isBusy;
      }
    }

    getDiscountElement(cartDrawer) {
      return cartDrawer?.querySelector('cart-discount') || null;
    }

    showFeedback(variant, message) {
      this.setInlineStatus(variant, message);
      this.showNotification(variant, message);
    }

    setInlineStatus(variant, message) {
      if (!this.statusEl) {
        return;
      }

      this.statusEl.hidden = !message;
      this.statusEl.classList.remove(
        'cart-drawer-subtotal__discount-status--success',
        'cart-drawer-subtotal__discount-status--error'
      );
      this.statusEl.classList.add(`cart-drawer-subtotal__discount-status--${variant}`);
      this.statusEl.textContent = message;

      window.clearTimeout(this.statusTimeoutId);
      this.statusTimeoutId = window.setTimeout(() => {
        if (this.statusEl) {
          this.statusEl.hidden = true;
          this.statusEl.textContent = '';
        }
      }, 6000);
    }

    showNotification(variant, message) {
      const notification = document.querySelector('#CartDrawerNotification');
      const alertVariant = variant === 'success' ? 'success' : 'warning';

      if (notification?.show) {
        notification.show(message, alertVariant, 6000);
        return;
      }

      console.warn(message);
    }
  }

  customElements.define('cart-discount', CartDiscount);
})();
